/**
 * Marketplace Basket Rollup Backfill — SuperAdmin operator tool.
 *
 * Two-step workflow:
 *   1. Preview (dry-run) — POST with `dry_run=true`. Walks the window, computes
 *      what would land, returns counts + any per-cell errors. Writes NO rollup
 *      rows; DOES write one audit row to the analytics-daily PLATFORM
 *      partition so the preview itself is traceable.
 *   2. Run backfill (live) — POST with `dry_run=false`. Same iteration but
 *      actually writes per-cell rollup rows. Idempotent — re-runnable.
 *
 * The "Run backfill" button is disabled until a successful preview has
 * completed for the SAME inputs. A confirmation modal gates the live call.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AlertCircle, CheckCircle2, Database, Home, Play, RefreshCw,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import MarketplaceAnalyticsTabs from '../components/marketplace/MarketplaceAnalyticsTabs';
import { useAuth } from '../context/AuthContext';
import marketplaceBackfillService, {
  type BackfillResponse,
  type BackfillStatusResponse,
} from '../services/marketplaceBackfillService';
import { cn } from '../utils/cn';

/** YYYY-MM-DD for the local calendar today — the default upper bound. */
const todayIso = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Default start: 30 days ago. Operators typically backfill recent windows;
 *  the 366-day cap is the upper bound the API enforces but a 30-day default
 *  keeps the first invocation fast (<1 min) and audit-friendly. */
const thirtyDaysAgoIso = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Day count between two YYYY-MM-DD strings, inclusive. Used to show the
 *  operator the window size BEFORE the 366-day cap rejects them upstream. */
const daysBetween = (start: string, end: string): number => {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  const ms = e.getTime() - s.getTime();
  if (Number.isNaN(ms) || ms < 0) return 0;
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
};

// Mirrors `MarketplaceBasketAnalytics.BACKFILL_MAX_RANGE_DAYS` in
// `libs/shared/shared/constants.py`. Keep both in lockstep — the backend
// rejects any window > this value with 400.
const MAX_RANGE_DAYS = 366;

interface ConfirmDialogProps {
  open: boolean;
  preview: BackfillResponse | null;
  onCancel: () => void;
  onConfirm: () => void;
  isRunning: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, preview, onCancel, onConfirm, isRunning,
}) => {
  // ESC closes the dialog (a11y); matches the focus-trap pattern other
  // hand-rolled dialogs in this codebase use.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isRunning) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, isRunning]);

  if (!open || !preview) return null;
  const cells = preview.cells_simulated ?? 0;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="backfill-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isRunning) onCancel();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <h2 id="backfill-confirm-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            Confirm backfill
          </h2>
        </div>
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
          <p>
            This will write <span className="font-bold tabular-nums">{cells}</span> rollup rows to{' '}
            <code className="text-xs">marketplace-basket-analytics-daily</code> for the window{' '}
            <span className="font-mono">{preview.start_date}</span> →{' '}
            <span className="font-mono">{preview.end_date}</span>.
          </p>
          <p>
            Re-runs are idempotent — existing rows are overwritten cleanly.
          </p>
          {(preview.errors ?? 0) > 0 && (
            <p className="text-amber-700 dark:text-amber-300">
              <strong>{preview.errors}</strong> cells failed during preview and will be retried during the run.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isRunning}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={isRunning}
            leftIcon={<Play className={cn('h-4 w-4', isRunning && 'animate-spin')} />}
          >
            {isRunning ? 'Running…' : 'Run backfill'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const MarketplaceBackfillPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();

  const [startDate, setStartDate] = useState<string>(thirtyDaysAgoIso());
  const [endDate, setEndDate] = useState<string>(todayIso());
  const [basketIdsCsv, setBasketIdsCsv] = useState<string>('');

  const [preview, setPreview] = useState<BackfillResponse | null>(null);
  const [liveResult, setLiveResult] = useState<BackfillResponse | null>(null);

  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Phase 4B polling state — after a live run kickoff returns 202, we poll
  // GET /admin/marketplace/backfill-rollups/{run_id} every 3s for progress
  // (rows_written / errors / total_cells) until status=COMPLETE. Polling
  // also stops if the user leaves the page or kicks off a new run.
  const [pollStatus, setPollStatus] = useState<BackfillStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = React.useRef<number | null>(null);
  const pollRunIdRef = React.useRef<string | null>(null);
  const pollStartRef = React.useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current != null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollRunIdRef.current = null;
    pollStartRef.current = null;
    setIsPolling(false);
  }, []);

  const startPolling = useCallback((runId: string) => {
    // 3-second interval; hard 10-minute ceiling so a stranded run doesn't
    // poll forever. Both constants are module-local (inside the callback)
    // so the dep array is just `stopPolling`.
    const POLL_INTERVAL_MS = 3000;
    const POLL_MAX_DURATION_MS = 10 * 60 * 1000;
    stopPolling();
    pollRunIdRef.current = runId;
    pollStartRef.current = Date.now();
    setIsPolling(true);

    const tick = async () => {
      // Bail if a parallel kickoff swapped the run_id or stopPolling fired.
      if (pollRunIdRef.current !== runId) return;
      try {
        const resp = await marketplaceBackfillService.getStatus(runId);
        if (pollRunIdRef.current !== runId) return;
        setPollStatus(resp);
        if (resp.status === 'COMPLETE') {
          stopPolling();
          return;
        }
      } catch (err: any) {
        // 404 transient at the very first poll if the kickoff audit-row
        // write hasn't propagated yet — don't kill the polling loop on a
        // single failure. Surface persistent errors only after the hard
        // ceiling.
        // eslint-disable-next-line no-console
        console.warn('backfill_status_poll_failed', err?.message);
      }
      const elapsed = Date.now() - (pollStartRef.current ?? Date.now());
      if (elapsed > POLL_MAX_DURATION_MS) {
        stopPolling();
      }
    };

    // Fire once immediately, then on interval.
    tick();
    pollIntervalRef.current = window.setInterval(tick, POLL_INTERVAL_MS);
  }, [stopPolling]);

  // Cleanup on unmount — important: a stranded interval would silently
  // keep firing setStates against an unmounted component, triggering React
  // warnings + leaking memory.
  React.useEffect(() => () => stopPolling(), [stopPolling]);

  // Track the inputs that produced the current preview so we can disable
  // the "Run backfill" button if the operator edits any input after preview.
  // Without this guard a preview against window A could be followed by a
  // live run against window B, breaking the "review what you're about to
  // ship" contract.
  const [previewInputs, setPreviewInputs] = useState<string | null>(null);

  const windowDays = useMemo(
    () => daysBetween(startDate, endDate),
    [startDate, endDate],
  );

  const basketIdsList = useMemo(
    () => basketIdsCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    [basketIdsCsv],
  );

  const currentInputsKey = useMemo(
    () => JSON.stringify({ startDate, endDate, basketIdsList }),
    [startDate, endDate, basketIdsList],
  );

  const previewStale = preview != null && previewInputs !== currentInputsKey;
  const canRun = preview != null && !previewStale && !isRunning;

  const validationError = useMemo(() => {
    if (!startDate || !endDate) return 'Both start and end dates are required';
    if (new Date(endDate) < new Date(startDate)) return 'End date must be on or after start date';
    if (windowDays > MAX_RANGE_DAYS) return `Window must be ≤ ${MAX_RANGE_DAYS} days; current is ${windowDays}`;
    return null;
  }, [startDate, endDate, windowDays]);

  const handlePreview = useCallback(async () => {
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsPreviewing(true);
    setError(null);
    setLiveResult(null);
    try {
      const resp = await marketplaceBackfillService.preview({
        start_date: startDate,
        end_date: endDate,
        basket_ids: basketIdsList.length > 0 ? basketIdsList : undefined,
      });
      setPreview(resp);
      setPreviewInputs(currentInputsKey);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Preview failed');
      setPreview(null);
      setPreviewInputs(null);
    } finally {
      setIsPreviewing(false);
    }
  }, [
    validationError, startDate, endDate, basketIdsList, currentInputsKey,
  ]);

  const handleRun = useCallback(async () => {
    setIsRunning(true);
    setError(null);
    setPollStatus(null);
    try {
      const resp = await marketplaceBackfillService.run({
        start_date: startDate,
        end_date: endDate,
        basket_ids: basketIdsList.length > 0 ? basketIdsList : undefined,
      });
      setLiveResult(resp);
      setConfirmOpen(false);
      // After a successful live run we clear the preview so the operator
      // must re-preview before another run — guards against accidental
      // double-execution.
      setPreview(null);
      setPreviewInputs(null);
      // Async kickoff → start polling for progress.
      if (resp.status === 'RUNNING' && resp.run_id) {
        startPolling(resp.run_id);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Backfill failed');
    } finally {
      setIsRunning(false);
    }
  }, [startDate, endDate, basketIdsList, startPolling]);

  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <StandardLayout>
      <PageHeader
        title="Marketplace Rollup Backfill"
        pageType="management"
        icon={<Database className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Marketplace Analytics', href: '/admin/marketplace-analytics' },
          { label: 'Backfill' },
        ]}
      />

      <MarketplaceAnalyticsTabs active="backfill" />

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-2 text-blue-800 dark:text-blue-200">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="text-sm space-y-1">
            <p>
              Recompute marketplace basket rollup rows over a date range. The aggregator math is
              the same one the nightly Lambda runs at 16:25 IST.
            </p>
            <p>
              <strong>Preview first</strong> to see what would be written. The live run is only
              enabled after a successful preview with identical inputs. Both preview and live
              runs are audited.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Backfill window</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Start date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="sm"
                disabled={isPreviewing || isRunning}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                End date
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="sm"
                disabled={isPreviewing || isRunning}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Window size
              </label>
              <div className={cn(
                'h-9 px-3 flex items-center text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 tabular-nums',
                windowDays > MAX_RANGE_DAYS && 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-700',
              )}>
                {windowDays} day{windowDays === 1 ? '' : 's'}
                {windowDays > MAX_RANGE_DAYS && ` (max ${MAX_RANGE_DAYS})`}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Basket IDs (optional, comma-separated)
            </label>
            <Input
              value={basketIdsCsv}
              onChange={(e) => setBasketIdsCsv(e.target.value)}
              placeholder="Leave empty to backfill ALL published baskets"
              size="sm"
              disabled={isPreviewing || isRunning}
            />
            {basketIdsList.length > 0 && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {basketIdsList.length} basket{basketIdsList.length === 1 ? '' : 's'} targeted
              </div>
            )}
          </div>

          {validationError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200">
              {validationError}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={isPreviewing || isRunning || validationError != null}
              leftIcon={<RefreshCw className={cn('h-4 w-4', isPreviewing && 'animate-spin')} />}
            >
              {isPreviewing ? 'Previewing…' : 'Preview (dry-run)'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={!canRun}
              leftIcon={<Play className="h-4 w-4" />}
              title={
                preview == null
                  ? 'Preview first'
                  : previewStale
                    ? 'Inputs changed — preview again before running'
                    : undefined
              }
            >
              Run backfill
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {isPreviewing && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Running preview…</span>
        </div>
      )}

      {preview && !isPreviewing && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle>
                Preview result {previewStale && (
                  <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
                    (stale — inputs changed)
                  </span>
                )}
              </CardTitle>
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                run_id: {preview.run_id.slice(0, 12)}…
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Window" value={`${preview.dates_in_window} day${preview.dates_in_window === 1 ? '' : 's'}`} />
              <Stat label="Baskets processed" value={String(preview.baskets_processed)} />
              <Stat label="Cells that would write" value={String(preview.cells_simulated ?? 0)} tone={(preview.cells_simulated ?? 0) > 0 ? 'pos' : 'neutral'} />
              <Stat label="Errors" value={String(preview.errors ?? 0)} tone={(preview.errors ?? 0) > 0 ? 'neg' : 'pos'} />
            </div>
            {preview.failed_cells && preview.failed_cells.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Failed cells (first {preview.failed_cells.length})
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300">Basket</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300">Date</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {preview.failed_cells.map((c, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono">{c.basket_id}</td>
                          <td className="px-3 py-2 font-mono">{c.target_date}</td>
                          <td className="px-3 py-2 text-red-700 dark:text-red-300">{c.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {liveResult && liveResult.status === 'RUNNING'
       && (pollStatus?.status ?? 'RUNNING') === 'RUNNING' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                  Backfill running
                </span>
              </CardTitle>
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                run_id: {liveResult.run_id.slice(0, 12)}…
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Live progress from the polled audit row. total_cells is
                  stamped at kickoff so the denominator renders immediately. */}
              {pollStatus && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat
                    label="Rows written"
                    value={String(pollStatus.rows_written ?? 0)}
                    tone="pos"
                  />
                  <Stat
                    label="Cells (total)"
                    value={String(pollStatus.total_cells ?? 0)}
                  />
                  <Stat
                    label="Baskets"
                    value={String(pollStatus.baskets_processed ?? 0)}
                  />
                  <Stat
                    label="Errors"
                    value={String(pollStatus.errors ?? 0)}
                    tone={(pollStatus.errors ?? 0) > 0 ? 'neg' : 'pos'}
                  />
                </div>
              )}
              {pollStatus && pollStatus.total_cells != null
               && pollStatus.total_cells > 0 && (
                <div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all"
                      style={{
                        width: `${Math.min(100, Math.round(((pollStatus.rows_written ?? 0) / pollStatus.total_cells) * 100))}%`,
                      }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 tabular-nums">
                    {pollStatus.rows_written ?? 0} of {pollStatus.total_cells} cells
                    {' '}({Math.min(100, Math.round(((pollStatus.rows_written ?? 0) / pollStatus.total_cells) * 100))}%)
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                {isPolling && (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                )}
                Polling every 3s · Started{' '}
                <span className="font-mono">{liveResult.started_at.slice(0, 19)}</span>
                {!isPolling && pollStatus?.status !== 'COMPLETE' && (
                  <span className="text-amber-600">
                    {' '}· Polling stopped — backfill may still be running. Check the BACKFILL_RUN audit row directly.
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completion card — render when either the sync response came back
          COMPLETE (small windows that fit under API GW timeout) OR the
          polling loop saw status=COMPLETE. */}
      {liveResult && (liveResult.status !== 'RUNNING'
                      || pollStatus?.status === 'COMPLETE') && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Backfill complete
                </span>
              </CardTitle>
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                run_id: {liveResult.run_id.slice(0, 12)}…
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {/* Async path: final counts come from the polled audit row.
                Sync path: counts come straight from the kickoff response. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Rows written" value={String(pollStatus?.rows_written ?? liveResult.rows_written ?? 0)} tone="pos" />
              <Stat label="Baskets processed" value={String(pollStatus?.baskets_processed ?? liveResult.baskets_processed ?? 0)} />
              <Stat label="Cells (total)" value={String(pollStatus?.total_cells ?? liveResult.cells_processed ?? 0)} />
              <Stat label="Errors" value={String(pollStatus?.errors ?? liveResult.errors ?? 0)} tone={((pollStatus?.errors ?? liveResult.errors) ?? 0) > 0 ? 'neg' : 'pos'} />
            </div>
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Started {(pollStatus?.started_at ?? liveResult.started_at).slice(0, 19)}
              {(pollStatus?.finished_at || liveResult.finished_at) && (
                <> · Finished {((pollStatus?.finished_at ?? liveResult.finished_at) || '').slice(0, 19)}</>
              )}
            </div>
            {pollStatus?.failed_items && pollStatus.failed_items.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Failed cells (first {pollStatus.failed_items.length})
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300">Basket</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300">Date</th>
                        <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-300">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {pollStatus.failed_items.map((c, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono">{c.basket_id}</td>
                          <td className="px-3 py-2 font-mono">{c.target_date}</td>
                          <td className="px-3 py-2 text-red-700 dark:text-red-300">{c.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        preview={preview}
        isRunning={isRunning}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleRun}
      />
    </StandardLayout>
  );
};

// ─── child components ───────────────────────────────────────────────────────

const Stat: React.FC<{ label: string; value: string; tone?: 'pos' | 'neg' | 'neutral' }> = ({
  label, value, tone = 'neutral',
}) => {
  const toneClass = tone === 'pos' ? 'text-green-600 dark:text-green-400'
    : tone === 'neg' ? 'text-red-600 dark:text-red-400'
    : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className={cn('text-xl font-bold mt-1 tabular-nums', toneClass)}>{value}</div>
    </div>
  );
};

export default MarketplaceBackfillPage;
